import type { CHANGES_STATE_ARR, ChangedFieldAction, ChangedField } from '../../actions/studio/fields/types';
import { CHANGES_STATE } from '../../actions/studio/fields/types';
import type { StudioFieldsActions } from '../../actions/studio/fields';
import type { FieldPath, LocalDocuments, LocalObjects, StatusType } from '../../types/Field';
import type { CSIObjectDefinition, ObjectDefinition, ObjectDefinitionWithFieldPath, ReferenceMap } from '../../types/Schema';
import type { FieldElement, PathMap, Sourcemaps } from '../../types/dom-types';
import { RESET_STUDIO_ACTION } from '../consts';
import type { State as FieldsValidationState } from './fields/types';
import { ValidationState } from './fields/types';

export type ApiCollectionObject = {
    srcObjectId: string;
    srcObjectLabel: string;
    hidden?: boolean;
    srcObjectUrl?: string;
    createdAt?: string;
    updatedAt?: string;
    updatedBy?: string[];
    referencedBy?: Array<CSIObjectDefinition & { count: number }>;
    locale?: string;
    status?: StatusType;
};

export type CollectionObject = ApiCollectionObject & {
    srcModelName: string;
    srcType: string;
    srcProjectId: string;
    srcObjectUrl?: string;
    srcModelLabel: string;
};

interface ApiTreeViewBaseNode {
    label: string;
    stableId: string;
    children?: ApiTreeViewNode[];
}

interface ApiTreeViewDocumentNode extends ApiTreeViewBaseNode {
    document: {
        srcType: string;
        srcProjectId: string;
        id: string;
    };
}

export type ApiTreeViewNode = ApiTreeViewBaseNode | ApiTreeViewDocumentNode;

interface TreeViewBaseNode {
    label: string;
    stableId: string;
    children?: TreeViewNode[];
}

interface TreeViewDocumentNode extends TreeViewBaseNode {
    document: {
        srcType: string;
        srcProjectId: string;
        id: string;
    } & Pick<CollectionObject, 'status' | 'createdAt' | 'updatedAt' | 'locale' | 'referencedBy' | 'srcModelName'>;
}

export type TreeViewNode = TreeViewBaseNode | TreeViewDocumentNode;

export type ApiObjectsCollection = {
    srcModelName: string;
    srcModelLabel: string;
    srcType: string;
    srcProjectId: string;
    objects: ApiCollectionObject[];
};

export type ObjectsCollection = Omit<ApiObjectsCollection, 'objects'> & {
    objects: CollectionObject[];
    allowedObjects: CollectionObject[];
};

export type LoadingField = {
    srcObjectId: string;
    srcType?: string;
    srcProjectId?: string;
    srcObjectLabel?: string;
} & (LoadingProgressField | LoadingFailedField);

export type LoadingProgressField = {
    isLoading: true;
};

export type LoadingFailedField = {
    isLoading: false;
};

export type HighlightAfterEdit = {
    action: ChangedFieldAction;
    fieldPath: FieldPath;
    highlightFieldPath: FieldPath;
    state: 'waitingForChange' | 'changeApplied' | 'waitingForHTML';
    anchorXpath?: string;
};

export const UPDATE_QUEUE_TYPE = {
    FIELDS: 'FIELDS',
    SCHEMA: 'SCHEMA',
    OBJECTS_CHANGES: 'OBJECTS_CHANGES'
} as const;

export type UpdateQueue = {
    [UPDATE_QUEUE_TYPE.SCHEMA]?: boolean;
    [UPDATE_QUEUE_TYPE.FIELDS]?: FetchFieldsOpts;
    [UPDATE_QUEUE_TYPE.OBJECTS_CHANGES]?: ObjectDefinition[];
};

export const FETCH_FIELDS_INITIATOR = {
    INIT: 'INIT',
    SOCKET_UPDATE: 'SOCKET_UPDATE',
    UPDATE_ENABLED: 'UPDATE_ENABLED',
    HTML_CHANGE: 'HTML_CHANGE',
    SCHEMA_CHANGE: 'SCHEMA_CHANGE',
    LOCALE_CHANGE: 'LOCALE_CHANGE'
} as const;

export type FETCH_FIELDS_INITIATOR_ARR = Array<keyof typeof FETCH_FIELDS_INITIATOR>;

export type FetchFieldsOpts = {
    initiator: keyof typeof FETCH_FIELDS_INITIATOR;
    useAnnotations: boolean;
    force?: boolean;
    updatedObjects?: ObjectDefinition[];
};

export interface AnnotationError {
    message: string;
    annotationXPath: string;
    elementXPath: string;
    attrValue: string;
    type: string;
}

export interface CollectionsFilters {
    scheduledActionId?: string;
}

export interface State {
    isFetching: boolean;
    fetchFieldsDataInitiator: keyof typeof FETCH_FIELDS_INITIATOR | null;
    fetchFieldsError: Error | null;
    fieldsErrors: AnnotationError[];
    annotationTree: $TSFixMe; // tstodo: annotaitonTree type
    clientAnnotationErrors: $TSFixMe[]; // tstodo: annotationErrors type
    pathMap: PathMap;
    sourcemaps: Sourcemaps;
    referenceMap: ReferenceMap;
    changed: ChangedField[];
    isUpdateEnabled: boolean;
    updateQueue: UpdateQueue;
    fieldsUpdateStartedDate: Date | null;
    elementForField: { [K: string]: FieldElement };
    isSyncingFields: boolean;
    isFetchingCollections: boolean | null;
    collections: ObjectsCollection[];
    collectionsObjects: CollectionObject[];
    styleObjectDef: ObjectDefinition | null;
    cmsObjects: ObjectDefinitionWithFieldPath[];
    currentPageObject: ObjectDefinition | null;
    currentPageObjects: ObjectDefinition[];
    objects: LocalObjects;
    documents: LocalDocuments;
    localDocuments: LocalDocuments;
    isConvertedDocs: boolean;
    localObjects: LocalObjects;
    highlightAfterEdit: HighlightAfterEdit | null;
    validations: FieldsValidationState;
    collectionsFilters: CollectionsFilters | null;
    changedObjects: ObjectDefinition[];
    treeViews: $TSFixMe;
}

const getDefaultState = (): State => ({
    isFetching: false,
    fetchFieldsDataInitiator: null,
    fetchFieldsError: null,
    fieldsErrors: [],
    annotationTree: {},
    clientAnnotationErrors: [],
    pathMap: {},
    sourcemaps: {},
    referenceMap: {},
    isUpdateEnabled: true,
    updateQueue: {},
    fieldsUpdateStartedDate: null,
    changed: [],
    isSyncingFields: false,

    /**
     * Map of element for the field with fieldPath. For instance, when there are 2 instance of the same field on the page, and user
     * selected second one (wysiwyg on page) - we are persisting it so next time user select it from the sidebar the same will be
     * selected
     */
    elementForField: {},

    isFetchingCollections: null,
    collections: [],
    collectionsObjects: [],
    styleObjectDef: null,

    cmsObjects: [],
    currentPageObject: null,
    currentPageObjects: [],
    objects: {},
    documents: [],
    localDocuments: [],
    isConvertedDocs: false,
    localObjects: {},
    highlightAfterEdit: null,
    validations: {
        data: {},
        state: ValidationState.Idle,
        initiator: null,
        linkedPaths: []
    },
    collectionsFilters: null,
    changedObjects: [],
    treeViews: []
});

const studioFieldsReducer = (state = getDefaultState(), action: StudioFieldsActions | null): State => {
    switch (action?.type) {
        case 'STUDIO_SET_CURRENT_PAGE_OBJECT': {
            return {
                ...state,
                currentPageObject: action.data.object
            };
        }

        case 'STUDIO_FIELDS_SET_UPDATE_ENABLED': {
            return {
                ...state,
                isUpdateEnabled: action.data?.isEnabled
            };
        }

        case 'SET_STUDIO_CHANGED_FIELDS': {
            const { changedFields } = action.data || {};
            const isSyncingFields =
                changedFields.filter((changedField) =>
                    ([CHANGES_STATE.REQUEST_SENT, CHANGES_STATE.REQUEST_SUCCESS] as CHANGES_STATE_ARR).includes(changedField.state)
                ).length > 0;
            return {
                ...state,
                changed: changedFields,
                isSyncingFields
            };
        }

        case 'STUDIO_FIELDS_SET_UPDATE_QUEUE': {
            return {
                ...state,
                updateQueue: action.data?.updateQueue ?? []
            };
        }

        case 'FETCH_FIELDS_STARTED': {
            const { initiator } = action.data || {};

            return {
                ...state,
                isFetching: true,
                fetchFieldsDataInitiator: initiator,
                fieldsUpdateStartedDate: new Date()
            };
        }

        case 'FETCH_FIELDS_FAILED': {
            return {
                ...state,
                isFetching: false,
                fetchFieldsDataInitiator: null,
                fetchFieldsError: action.data?.error
            };
        }

        case 'FETCH_FIELDS_SUCCEED': {
            const { pathMap, currentPageObjects, sourcemaps, referenceMap } = action.data || {};
            return {
                ...state,
                isFetching: false,
                fetchFieldsDataInitiator: null,
                fetchFieldsError: null,
                pathMap: pathMap || state.pathMap,
                currentPageObjects,
                sourcemaps,
                referenceMap
            };
        }

        case 'FETCH_FIELDS_RESET': {
            return {
                ...state,
                isFetching: false,
                fetchFieldsDataInitiator: null,
                fetchFieldsError: null,
                pathMap: {},
                currentPageObjects: [],
                sourcemaps: {},
                referenceMap: {}
            };
        }

        case 'SET_STUDIO_FIELDS_CURRENT_PAGE_OBJECTS': {
            const { currentPageObjects } = action.data;
            return {
                ...state,
                currentPageObjects
            };
        }

        case 'FETCH_FIELDS_SET_ANNOTATION_ERRORS': {
            const { errors } = action.data || {};
            return {
                ...state,
                fieldsErrors: errors
            };
        }

        case 'SET_ELEMENT_FOR_FIELD': {
            const { fieldPathStr, xpath, type } = action.data || {};
            const elementForField = { ...state.elementForField };
            elementForField[fieldPathStr] = { xpath, type };
            return {
                ...state,
                elementForField
            };
        }

        case 'SET_ELEMENTS_FOR_FIELDS': {
            return {
                ...state,
                elementForField: action.data.elementForField || {}
            };
        }

        case 'STUDIO_FIELDS_SET_FETCHING_COLLECTIONS': {
            return {
                ...state,
                isFetchingCollections: action.data.isFetchingCollections
            };
        }

        case 'STUDIO_FIELDS_SET_COLLECTIONS': {
            return {
                ...state,
                isFetchingCollections: false,
                collections: action.data.collections,
                styleObjectDef: action.data.styleObjectDef,
                treeViews: action.data.treeViews,
                collectionsObjects: action.data.collectionsObjects
            };
        }

        case 'SET_FIELDS_CMS_OBJECTS_IDS': {
            return {
                ...state,
                cmsObjects: action.data || []
            };
        }

        case 'SET_FIELDS_OBJECTS': {
            return {
                ...state,
                objects: action.data
            };
        }

        case 'SET_DOCUMENTS': {
            return {
                ...state,
                documents: action.data.documents,
                isConvertedDocs: action.data.isConvertedDocs
            };
        }

        case 'SET_FIELDS_LOCAL_OBJECTS': {
            return {
                ...state,
                localObjects: action.data
            };
        }

        case 'SET_FIELDS_LOCAL_DOCUMENTS': {
            return {
                ...state,
                localDocuments: action.data.documents
            };
        }

        case 'SET_ANNOTATION_TREE': {
            const { annotationTree, clientAnnotationErrors, sourcemaps } = action.data || {};
            return {
                ...state,
                annotationTree,
                clientAnnotationErrors,
                sourcemaps
            };
        }

        case 'STUDIO_SET_HIGHLIGHT_AFTER_EDIT': {
            return {
                ...state,
                highlightAfterEdit: action.data
            };
        }

        case 'STUDIO_FIELDS_SET_COLLECTIONS_FILTER': {
            return {
                ...state,
                collectionsFilters: action.data
            };
        }

        case 'STUDIO_SET_CHANGED_OBJECTS': {
            return {
                ...state,
                changedObjects: action.data.changedObjects
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioFieldsReducer;
